import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, ListSubheader,
  MenuItem, Paper, Step, StepLabel, Stepper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { safeDecode } from '../utils/decode';
import { numeric } from '../quotas/QuotaTable';
import CreatePreview from './CreatePreview';
import { questionMenu } from './logicModel';
import {
  MAX_DIMENSIONS, SET_KINDS, addBack, calculateLimits, cellLogic, cellName, dimensionProblems, leaveOut, mergeBands,
  newDimension, parsePaste, percentProblems, renameBand, setCells, splitBand,
} from './quotaSets';

const STEPS = ['Questions and bands', 'Limits', 'Check and create'];
const EMPTY = { questionId: null, bands: [], leftOut: [] };
const WHOLE = /^\d+$/;

/** A question's name for menus and headers; a grid row is "<grid> - <row>". */
const questionTitle = (question, byId) => {
  const parent = byId.get(question.parent_question_id);
  const title = safeDecode(question.title);
  return parent ? `${safeDecode(parent.title)} - ${title}` : title;
};

const BandsEditor = ({ dimension, question, onChange }) => {
  const [selected, setSelected] = useState([]);
  const title = (optionId) => safeDecode(question.options.find((o) => o.option_id === optionId)?.title ?? String(optionId));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          Each answer is its own band. Tick bands and merge them (e.g. 18-24 and 25-34 into 18-34), or remove an answer to leave it out.
        </Typography>
        <Button size="small" variant="outlined" disabled={selected.length < 2} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          onClick={() => { onChange(mergeBands(dimension, selected)); setSelected([]); }}>
          Merge selected
        </Button>
      </Box>
      {dimension.bands.map((band) => (
        <Box key={band.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, flexWrap: 'wrap' }}>
          <Checkbox size="small" checked={selected.includes(band.id)} onChange={() => toggle(band.id)}
            inputProps={{ 'aria-label': `Select band ${band.label}` }} />
          <TextField size="small" value={band.label} onChange={(e) => onChange(renameBand(dimension, band.id, e.target.value))}
            inputProps={{ 'aria-label': 'Band name' }} error={!band.label.trim()} sx={{ width: 200 }} />
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
            {band.optionIds.map((optionId) => (
              <Chip key={optionId} size="small" label={title(optionId)} onDelete={() => onChange(leaveOut(dimension, optionId))}
                deleteIcon={<Tooltip title="Leave this answer out"><CloseRoundedIcon /></Tooltip>} />
            ))}
          </Box>
          {band.optionIds.length > 1 && (
            <Tooltip title="Split into one band per answer">
              <IconButton size="small" aria-label={`Split ${band.label}`} onClick={() => onChange(splitBand(dimension, band.id, question))}>
                <CallSplitRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}
      {dimension.leftOut.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Left out (no quota covers them):</Typography>
          {dimension.leftOut.map((optionId) => (
            <Chip key={optionId} size="small" variant="outlined" label={title(optionId)} icon={<AddRoundedIcon />}
              onClick={() => onChange(addBack(dimension, optionId, question))} aria-label={`Add back ${title(optionId)}`} />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * The quota set builder (design §4): one to three questions with answer bands, a limit per
 * combination, then Wrangler's preview and an all-or-nothing create (in batches if large).
 */
const QuotaSetBuilder = ({ surveyId, questions, existingSets = [], onCreated, onClose }) => {
  const byId = useMemo(() => new Map(questions.map((q) => [q.question_id, q])), [questions]);
  const menu = useMemo(() => {
    const items = questionMenu(questions).filter((item) => item.type === 'heading' || SET_KINDS.has(item.question.kind));
    return items.filter((item, i) => item.type !== 'heading' || items[i + 1]?.indent);
  }, [questions]);

  const [step, setStep] = useState(0);
  const [dimensions, setDimensions] = useState([EMPTY]);
  const [setName, setSetName] = useState('');
  const [limits, setLimits] = useState({});
  const [isOn, setIsOn] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  const [total, setTotal] = useState('');
  const [percents, setPercents] = useState({});
  const [drafts, setDrafts] = useState(null);

  const chosen = dimensions.filter((d) => d.questionId != null);
  const cells = setCells(chosen);
  const longest = cells.reduce((n, c) => Math.max(n, cellName(setName, c.bands).length), 0);
  const problems = [
    ...(setName.trim() ? [] : ['Name the set.']),
    ...dimensions.map((d, i) => (d.questionId == null ? `Question ${i + 1}: choose a question.` : null)).filter(Boolean),
    ...dimensionProblems(chosen),
    ...(longest > 255 ? ['Some quota names would be longer than 255 characters: shorten the set name or the bands.'] : []),
  ];
  // Splitting a total by percentages assumes the questions are independent, which is rarely
  // true of real targets, so it's only offered for a single question.
  const canSplit = chosen.length === 1;

  const limitText = (cell) => limits[cell.key] ?? '';
  const badLimits = cells.filter((c) => limitText(c) !== '' && !WHOLE.test(limitText(c)));
  const filled = cells.filter((c) => WHOLE.test(limitText(c)));
  const zeros = filled.filter((c) => Number(limitText(c)) === 0);
  const calcProblems = percentProblems(chosen, percents);
  const totalValid = WHOLE.test(total) && Number(total) > 0;

  const setDimension = (i, value) => setDimensions((ds) => ds.map((d, j) => (j === i ? value : d)));
  const pickQuestion = (i, questionId) => setDimension(i, newDimension(byId.get(questionId)));

  const paste = (e, start) => {
    const grid = parsePaste(e.clipboardData.getData('text'));
    if (grid.length === 1 && grid[0].length === 1) return; // one value: an ordinary paste
    e.preventDefault();
    setLimits((prev) => {
      const next = { ...prev };
      grid.flat().forEach((value, j) => {
        const cell = cells[start + j];
        if (cell) next[cell.key] = value.replace(/[,\s]/g, '');
      });
      return next;
    });
  };

  const fillFromTotal = () => {
    const calculated = calculateLimits(chosen, Number(total), percents);
    setLimits(Object.fromEntries(Object.entries(calculated).map(([k, v]) => [k, String(v)])));
  };

  const toPreview = () => {
    setDrafts(filled.map((cell) => ({
      name: cellName(setName, cell.bands), description: '', logic: cellLogic(chosen, cell.bands),
      original_limit: Number(limitText(cell)), is_on: isOn, set_name: setName.trim(),
    })));
    setStep(2);
  };

  return (
    <Dialog open onClose={step === 2 ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1 }}>New quota set</Box>
        <IconButton onClick={onClose} aria-label="Close"><CloseRoundedIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>

        {step === 0 && (
          <Box>
            <TextField label="Set name" size="small" required value={setName} autoFocus
              onChange={(e) => setSetName(e.target.value)} sx={{ mb: 2.5, width: { xs: '100%', sm: 420 } }}
              placeholder="e.g. Age x Gender"
              helperText={cells.length
                ? `Short: it starts every quota's name. ${cells.length} cell${cells.length !== 1 ? 's' : ''}, named like "${cellName(setName || '<set name>', cells[0].bands)}"`
                : "Short: it starts every quota's name, e.g. \"Age x Gender - 18-34 x Woman\"."} />
            {dimensions.map((dimension, i) => {
              const question = byId.get(dimension.questionId);
              return (
                <Paper key={i} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: question ? 1.5 : 0 }}>
                    <TextField select size="small" label={`Question ${i + 1}`} value={dimension.questionId ?? ''} sx={{ flex: 1 }}
                      onChange={(e) => pickQuestion(i, Number(e.target.value))}>
                      {menu.map((item) => (item.type === 'heading'
                        ? <ListSubheader key={item.key}>{safeDecode(item.question.title)}</ListSubheader>
                        : (
                          <MenuItem key={item.key} value={item.question.question_id} sx={item.indent ? { pl: 4 } : null}
                            disabled={chosen.some((d, j) => j !== i && d.questionId === item.question.question_id)}>
                            {safeDecode(item.question.title)}
                          </MenuItem>
                        )))}
                    </TextField>
                    {dimensions.length > 1 && (
                      <Tooltip title="Remove this question">
                        <IconButton aria-label={`Remove question ${i + 1}`} onClick={() => setDimensions((ds) => ds.filter((_, j) => j !== i))}>
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  {question?.kind === 'multi-select' && (
                    <Alert severity="warning" sx={{ mb: 1.5 }}>
                      People can choose several answers here, so cells overlap: one respondent can count in several quotas of the set.
                    </Alert>
                  )}
                  {question && <BandsEditor dimension={dimension} question={question} onChange={(d) => setDimension(i, d)} />}
                </Paper>
              );
            })}
            <Button startIcon={<AddRoundedIcon />} disabled={dimensions.length >= MAX_DIMENSIONS}
              onClick={() => setDimensions((ds) => [...ds, EMPTY])} sx={{ textTransform: 'none', mb: 2 }}>
              Add a question{dimensions.length >= MAX_DIMENSIONS ? ' (three at most)' : ''}
            </Button>

            {existingSets.includes(setName.trim()) && (
              <Alert severity="info" sx={{ mt: 1 }}>
                This survey already has a set with this name. These quotas join it, and rules scoped to it cover them too.
              </Alert>
            )}
            {problems.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{problems[0]}</Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
              <Button variant="contained" disabled={problems.length > 0} onClick={() => setStep(1)} sx={{ textTransform: 'none' }}>Next: limits</Button>
            </Box>
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 280 }}>
                Type a limit for each cell, or paste your targets from Excel: a column, or a block in the same order, into the
                first cell. A blank cell creates no quota.
              </Typography>
              {canSplit && (
                <Button size="small" variant={calcOpen ? 'contained' : 'outlined'} onClick={() => setCalcOpen((o) => !o)} sx={{ textTransform: 'none' }}>
                  Split a total by percentages
                </Button>
              )}
            </Box>
            {canSplit && calcOpen && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Each band gets its percentage of the total, rounded so the limits add up to the total exactly.
                </Typography>
                <TextField size="small" label="Total" value={total} onChange={(e) => setTotal(e.target.value.trim())}
                  error={total !== '' && !totalValid} sx={{ width: 140, mb: 2 }} />
                {chosen.map((d, i) => {
                  const sum = d.bands.reduce((s, b) => s + (Number(percents[b.id]) || 0), 0);
                  return (
                    <Box key={d.questionId} sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        {questionTitle(byId.get(d.questionId), byId)}{' '}
                        <Typography component="span" variant="body2" color={Math.abs(sum - 100) > 0.01 ? 'error' : 'text.secondary'}>
                          ({Math.round(sum * 100) / 100}%)
                        </Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {d.bands.map((b) => (
                          <TextField key={b.id} size="small" label={`${b.label} %`} value={percents[b.id] ?? ''} sx={{ width: 130 }}
                            onChange={(e) => setPercents((p) => ({ ...p, [b.id]: e.target.value.trim() }))} />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
                {(calcProblems[0] || !totalValid) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{totalValid ? calcProblems[0] : 'Enter a total.'}</Typography>
                )}
                <Button variant="outlined" size="small" disabled={!totalValid || calcProblems.length > 0} onClick={fillFromTotal}
                  sx={{ textTransform: 'none' }}>
                  Fill in the limits
                </Button>
              </Paper>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 440, mb: 1.5 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {chosen.map((d) => <TableCell key={d.questionId}>{questionTitle(byId.get(d.questionId), byId)}</TableCell>)}
                    <TableCell>Quota name</TableCell>
                    <TableCell sx={numeric}>Limit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cells.map((cell, i) => {
                    const name = cellName(setName, cell.bands);
                    const text = limitText(cell);
                    return (
                      <TableRow key={cell.key}>
                        {cell.bands.map((b) => <TableCell key={b.id}>{b.label}</TableCell>)}
                        <TableCell sx={{ color: text === '' ? 'text.disabled' : 'text.primary' }}>{name}</TableCell>
                        <TableCell sx={{ ...numeric, py: 0.5 }}>
                          <TextField size="small" value={text} onPaste={(e) => paste(e, i)}
                            onChange={(e) => setLimits((l) => ({ ...l, [cell.key]: e.target.value.trim() }))}
                            error={text !== '' && !WHOLE.test(text)}
                            inputProps={{ 'aria-label': `Limit for ${name}`, inputMode: 'numeric', style: { textAlign: 'right' } }}
                            sx={{ width: 100 }} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" color="text.secondary">
              {filled.length} of {cells.length} cells have a limit
              {filled.length > 0 && `, adding up to ${filled.reduce((s, c) => s + Number(limitText(c)), 0).toLocaleString()}`}.
            </Typography>
            {badLimits.length > 0 && <Alert severity="error" sx={{ mt: 1 }}>Limits are whole numbers, 0 or more: check the cells marked red.</Alert>}
            {zeros.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {zeros.length} cell{zeros.length !== 1 ? 's have' : ' has'} a limit of 0, which screens out everyone in {zeros.length !== 1 ? 'them' : 'it'} once ON.
              </Alert>
            )}
            <FormControlLabel sx={{ mt: 1.5, display: 'flex' }}
              control={<Checkbox checked={isOn} onChange={(e) => setIsOn(e.target.checked)} />}
              label="Switch them ON straight away" />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              {isOn ? 'They block respondents as soon as they fill.' : 'Left unticked, they start OFF: they count, but block nobody until switched ON.'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              <Button onClick={() => setStep(0)} sx={{ textTransform: 'none' }}>Back</Button>
              <Button variant="contained" disabled={filled.length === 0 || badLimits.length > 0} onClick={toPreview} sx={{ textTransform: 'none' }}>
                Next: check
              </Button>
            </Box>
          </Box>
        )}

        {step === 2 && drafts && (
          <CreatePreview surveyId={surveyId} drafts={drafts} questions={questions} onCreated={onCreated}
            onBack={() => setStep(1)} onDone={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QuotaSetBuilder;
